import React, { useState, useEffect, useRef, useCallback } from "react";
import GameMap from "./GameMap";
import "../styles/CommunityMap.css";

// For debugging - enable verbose console logging
const DEBUG = true;

// Helper function to get appropriate CSS ball class based on ball type
const getBallClass = (ballType) => {
  switch (Number(ballType)) {
    case 1: return "pokeball";
    case 2: return "greatball";
    case 3: return "ultraball";
    case 4: return "longball";
    case 5: return "rangeball";
    default: return "pokeball";
  }
};

const CommunityGameMap = ({ config, mapType }) => {
  const [otherPlayers, setOtherPlayers] = useState([]);
  const [username, setUsername] = useState("");
  const [sharedPokemon, setSharedPokemon] = useState([]);
  const [capturedPokemonIds, setCapturedPokemonIds] = useState(new Set());
  const [captureEffects, setCaptureEffects] = useState([]);
  const wsRef = useRef(null);

  // Function to log state changes for debugging
  const logState = useCallback((action, data) => {
    if (DEBUG) {
      console.group(`[CommunityMap] ${action}`);
      console.log(data);
      console.groupEnd();
    }
  }, []);

  useEffect(() => {
    // Get the current username
    const authUser = localStorage.getItem("authUser");
    setUsername(authUser);
    
    logState("Initializing", { authUser, mapId: config.id, mapType });
    
    // Initialize community websocket connection
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const host = window.location.hostname === "localhost" ? "localhost:8000" : "pokemonradiance.com";
    const wsUrl = `${proto}://${host}/ws/community`;
    
    console.log(`%c[WebSocket] Connecting to ${wsUrl}`, "color: blue; font-weight: bold;");
    wsRef.current = new WebSocket(wsUrl);
    
    // Make the WebSocket available to other components
    window.communityWS = wsRef.current;
    
    wsRef.current.onopen = () => {
      console.log("%c[WebSocket] Connection opened", "color: green; font-weight: bold;");
      
      if (wsRef.current.readyState === 1) {
        const joinMessage = {
          map_id: config.id,
          username: authUser,
          action: "join",
          mapType: mapType
        };
        
        console.log("%c[WebSocket] Sending join message", "color: blue;", joinMessage);
        wsRef.current.send(JSON.stringify(joinMessage));
      } else {
        console.warn("%c[WebSocket] Not ready on open event", "color: orange; font-weight: bold;");
      }
    };

    wsRef.current.onmessage = ({ data }) => {
      try {
        const message = JSON.parse(data);
        console.log("%c[WebSocket] Message received", "color: green;", message);
        
        // Handle player position updates
        if (message.type === "player_positions" && Array.isArray(message.players)) {
          // Debug the raw message
          console.log("%c[Players] Raw message", "color: purple;", JSON.stringify(message.players));
          
          // Filter out the current player from the list
          const filteredPlayers = message.players.filter(
            player => player && player.username && player.username !== authUser
          );
          
          console.log("%c[Players] Filtered players", "color: blue;", {
            count: filteredPlayers.length,
            players: filteredPlayers,
            currentUser: authUser
          });
          
          // Validate player data
          const validPlayers = filteredPlayers.every(p => 
            p.position && 
            typeof p.position.x === 'number' && 
            typeof p.position.y === 'number' && 
            p.direction
          );
          
          if (!validPlayers) {
            console.warn("%c[Players] Invalid player data detected", "color: red; font-weight: bold;", filteredPlayers);
          }
          
          // Always update state with what we have
          console.log("%c[State] Updating otherPlayers state", "color: orange;", filteredPlayers);
          
          // Force a complete state update with new array reference
          setOtherPlayers([...filteredPlayers]);
          
          // Debug current state after update
          setTimeout(() => {
            console.log("%c[State] State after update", "color: green;", { 
              otherPlayers: document.querySelectorAll('.other-player').length,
              players: filteredPlayers.length
            });
          }, 50);
        }
        
        // Handle captured pokemon messages
        else if (message.type === "captured_pokemon" && Array.isArray(message.pokemon_ids)) {
          console.log("%c[Pokemon] Captured Pokemon IDs", "color: green;", message.pokemon_ids);
          // Update captured Pokemon IDs set
          const newCapturedIds = new Set(message.pokemon_ids);
          setCapturedPokemonIds(newCapturedIds);
          logState("Updated capturedPokemonIds", { ids: Array.from(newCapturedIds) });
        }
        
        // Handle shared pokemon spawns
        else if (message.type === "pokemon_spawn" && Array.isArray(message.pokemon)) {
          console.log("%c[Pokemon] Shared Pokemon spawns", "color: green;", message.pokemon);
          setSharedPokemon(message.pokemon);
          logState("Updated sharedPokemon", { count: message.pokemon.length, pokemon: message.pokemon });
        }
        
        // Handle pokemon captured event (when any player captures a Pokemon)
        else if (message.type === "pokemon_captured") {
          console.log("%c[Pokemon] Pokemon captured", "color: green;", message);
          const { pokemon_id, captured_by, species } = message;
          
          // Add to captured set
          setCapturedPokemonIds(prev => new Set([...Array.from(prev), pokemon_id]));
          
          // Update shared Pokemon list to mark this Pokemon as captured
          setSharedPokemon(prevPokemon => 
            prevPokemon.filter(pokemon => pokemon.id !== pokemon_id)
          );
          
          // Add a capture success effect if it wasn't captured by the current player
          if (captured_by !== authUser) {
            // Find the Pokemon position before removing it
            const capturedPokemon = sharedPokemon.find(p => p.id === pokemon_id);
            if (capturedPokemon) {
              setCaptureEffects(prev => [
                ...prev,
                {
                  id: `${pokemon_id}-success-${Date.now()}`,
                  pokemonId: pokemon_id,
                  ballClass: 'pokeball', // Default to pokeball if we don't know the ball type
                  ballType: 1,
                  username: captured_by,
                  type: "success",
                  position: { x: capturedPokemon.x, y: capturedPokemon.y },
                  species: species
                }
              ]);
            }
          }
          
          logState("Pokemon captured", { id: pokemon_id, by: captured_by });
        }
        
        // Handle pokemon capturing event (when a player attempts to capture)
        else if (message.type === "pokemon_capturing") {
          console.log("%c[Pokemon] Pokemon capturing", "color: green;", {
            id: message.pokemon_id,
            username: message.username,
            ballType: message.ball_type
          });
          
          // Don't show animation for the current player (they'll see their own)
          if (message.username !== authUser) {
            // Add a capture effect
            const ballClass = getBallClass(message.ball_type);
            setCaptureEffects(prev => [
              ...prev,
              {
                id: `${message.pokemon_id}-${Date.now()}`,
                pokemonId: message.pokemon_id,
                ballClass,
                ballType: message.ball_type,
                username: message.username,
                type: "capturing"
              }
            ]);
            
            logState("Added capture effect", { pokemonId: message.pokemon_id, ballClass });
          }
        }
        
        // Handle pokemon breakout event
        else if (message.type === "pokemon_breakout") {
          console.log("%c[Pokemon] Pokemon breakout", "color: green;", {
            id: message.pokemon_id,
            username: message.username,
            ballType: message.ball_type,
            position: message.position
          });
          
          // Don't show animation for the current player (they'll see their own)
          if (message.username !== authUser) {
            const ballClass = getBallClass(message.ball_type);
            setCaptureEffects(prev => [
              ...prev,
              {
                id: `${message.pokemon_id}-breakout-${Date.now()}`,
                pokemonId: message.pokemon_id,
                ballClass,
                ballType: message.ball_type,
                username: message.username,
                type: "breakout",
                position: message.position
              }
            ]);
            
            logState("Added breakout effect", { pokemonId: message.pokemon_id, ballClass });
            
            // Clean up the effect after animation completes
            setTimeout(() => {
              setCaptureEffects(prev => 
                prev.filter(effect => effect.id !== `${message.pokemon_id}-breakout-${Date.now()}`)
              );
            }, 3000); // 3 seconds should be enough for the animation
          }
        }
        
        // Handle pokemon fled event
        else if (message.type === "pokemon_fled") {
          console.log("%c[Pokemon] Pokemon fled", "color: green;", {
            id: message.pokemon_id,
            username: message.username
          });
          
          // Remove the Pokemon from the shared list
          setSharedPokemon(prev => prev.filter(p => p.id !== message.pokemon_id));
          logState("Pokemon fled", { id: message.pokemon_id });
        }
      } catch (error) {
        console.error("%c[WebSocket] Error processing message", "color: red; font-weight: bold;", error, data);
      }
    };

    wsRef.current.onerror = (e) => {
      console.error("Community WebSocket error:", e);
    };

    wsRef.current.onclose = () => {
      console.log("Community WS closed");
    };

    // Cleanup on unmount
    return () => {
      if (wsRef.current && wsRef.current.readyState === 1) {
        const leaveMessage = {
          action: "leave",
          map_id: config.id,
          username: username,
          mapType: mapType
        };
        
        if (DEBUG) {
          console.log("Sending leave message:", leaveMessage);
        }
        
        wsRef.current.send(JSON.stringify(leaveMessage));
        wsRef.current.close();
        
        if (DEBUG) {
          console.log("WebSocket closed");
        }
      }
    };
  }, [config.id, mapType, username, logState]);

  // Function to update player position to the server
  const updatePosition = (position, direction) => {
    if (!position || !direction) {
      console.warn("%c[Position] Invalid position or direction", "color: red;", { position, direction });
      return;
    }
    
    if (wsRef.current && wsRef.current.readyState === 1) {
      // Create a deep copy of position to avoid reference issues
      const positionCopy = { 
        x: Math.round(position.x), // Ensure integers
        y: Math.round(position.y)
      };
      
      const message = {
        map_id: config.id,
        username: username,
        action: "position_update",
        position: positionCopy,
        direction: direction,
        mapType: mapType
      };
      
      console.log("%c[Position] Sending update", "color: blue;", { 
        x: positionCopy.x, 
        y: positionCopy.y, 
        dir: direction 
      });
      
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn("%c[WebSocket] Not ready for position update", "color: orange;", {
        readyState: wsRef.current?.readyState,
        username,
        position,
        direction
      });
    }
  };

  // Clean up capture effects after they're done
  useEffect(() => {
    if (captureEffects.length > 0) {
      const timer = setTimeout(() => {
        // Remove capturing effects after 10 seconds (should be plenty of time)
        setCaptureEffects(prev => 
          prev.filter(effect => 
            Date.now() - parseInt(effect.id.split('-')[1]) < 10000
          )
        );
      }, 10000);
      
      return () => clearTimeout(timer);
    }
  }, [captureEffects]);

  // Send pokemon_capturing event to server
  const handlePokemonCapturing = useCallback((spawnId, ballType) => {
    if (wsRef.current && wsRef.current.readyState === 1) {
      const message = {
        action: "pokemon_capturing",
        map_id: config.id,
        username,
        spawn_id: spawnId,
        ball_type: ballType
      };
      
      wsRef.current.send(JSON.stringify(message));
      logState("Sent pokemon_capturing", message);
    }
  }, [config.id, username, logState]);

  // Send pokemon_breakout event to server
  const handlePokemonBreakout = useCallback((spawnId, ballType, position) => {
    if (wsRef.current && wsRef.current.readyState === 1) {
      const message = {
        action: "pokemon_breakout",
        map_id: config.id,
        username,
        spawn_id: spawnId,
        ball_type: ballType,
        position
      };
      
      wsRef.current.send(JSON.stringify(message));
      logState("Sent pokemon_breakout", message);
    }
  }, [config.id, username, logState]);

  // Send pokemon_fled event to server
  const handlePokemonFled = useCallback((spawnId) => {
    if (wsRef.current && wsRef.current.readyState === 1) {
      const message = {
        action: "pokemon_fled",
        map_id: config.id,
        username,
        spawn_id: spawnId
      };
      
      wsRef.current.send(JSON.stringify(message));
      logState("Sent pokemon_fled", message);
    }
  }, [config.id, username, logState]);

  return (
    <GameMap 
      config={config} 
      otherPlayers={otherPlayers} 
      isCommunity={true}
      updatePosition={updatePosition}
      sharedPokemon={sharedPokemon}
      capturedPokemonIds={capturedPokemonIds}
      captureEffects={captureEffects}
      onPokemonCapturing={handlePokemonCapturing}
      onPokemonBreakout={handlePokemonBreakout}
      onPokemonFled={handlePokemonFled}
      communityWs={wsRef.current}
    />
  );
};

export default CommunityGameMap;
